/**
 * AIConsentPolicyContent — the AI-specific disclosure shown from the
 * AI Consent Gate ONLY. Deliberately separate from the general
 * Terms+Privacy at signup so the user never reads the same thing twice.
 *
 * Focus: Apple Guidelines 5.1.1(i) & 5.1.2(i) — what AI processing
 * looks like, what data leaves the app, where it goes, and how the
 * user withdraws. Nothing else. No account rules, no billing, no
 * general onboarding text.
 */

function H({ children }) {
  return (
    <h2 className="text-[15px] font-extrabold text-[#0A192F] mt-4 mb-1.5">
      {children}
    </h2>
  );
}

function P({ children }) {
  return (
    <p className="text-[12.5px] text-[#3F4750] leading-[1.85] mb-2">
      {children}
    </p>
  );
}

function UL({ items }) {
  return (
    <ul className="list-disc pr-5 space-y-1 mb-2">
      {items.map((t, i) => (
        <li key={i} className="text-[12px] text-[#3F4750] leading-[1.75]">
          {t}
        </li>
      ))}
    </ul>
  );
}

export default function AIConsentPolicyContent() {
  return (
    <div className="text-right" dir="rtl" data-testid="ai-consent-policy-content">
      <div className="text-[11px] text-[#5A6470] mb-1">
        الإصدار 1.0 — 22 يوليو 2026
      </div>
      <h1 className="text-[18px] font-extrabold text-[#0A192F] mb-3">
        سِياسة استِخدام الذَّكاء الاصطِناعي في LexIQ
      </h1>

      <P>
        هٰذِه السِّياسة تَخُصّ حَصراً المِيزات الذَّكيّة في التَّطبيق. تَنطَبِق عَلَيها
        شُروط الاستِخدام العامّة التي وافَقتَ عَلَيها عِند التَّسجيل.
      </P>

      <H>ما هي المِيزات الذَّكيّة</H>
      <UL items={[
        "المُحادَثة القانونيّة الذَّكيّة.",
        "تَحليل مُحتَوى المُستَنَدات.",
        "قِراءَة نُصوص الصُّوَر (OCR).",
        "تَحويل التَّسجيلات الصَّوتيّة إلى نُصوص.",
      ]} />

      <H>ما الذي يُرسَل</H>
      <P>فَقَط ما تُدخِله أَنت طَواعيةً:</P>
      <UL items={[
        "نَصّ سُؤالك في المُحادَثة.",
        "مُحتَوى المُستَنَد الذي تَطلُب تَحليله.",
        "الصُّوَر التي تَختار مَسحها.",
        "التَّسجيلات الصَّوتيّة التي تَختار تَحويلها.",
      ]} />

      <H>ما الذي لا يُرسَل</H>
      <UL items={[
        "اسمك، بَريدك، رَقم هاتفك.",
        "بَيانات مُوَكِّليك المَحفوظة في التَّطبيق.",
        "قَضاياك أَو أَحداث جَدولك.",
        "أَيّ شَيء لَم تُدرِجه أَنت في السُّؤال مُباشَرَةً.",
      ]} />

      <H>حِمايَة بَياناتك</H>
      <UL items={[
        "الإرسال مُشَفَّر TLS 1.3.",
        "لا تُستَخدَم بَياناتك لِتَدريب النَّماذِج (شَرط تَعاقُدي).",
        "تُحذَف مِن خَوادِم المُعالَجة خِلال 30 يوماً كَحَدّ أَقصى.",
        "لا تُشارَك مَع أَيّ طَرَف ثالث خارِج مُعالَجة سُؤالك.",
      ]} />

      <H>مَسؤوليَّتُك المِهَنيّة</H>
      <UL items={[
        "مُخرَجات الذَّكاء الاصطِناعي ليست اِستِشارة قانونيّة مُلزِمة.",
        "يَجِب مُراجَعَة كُلّ نَتيجة قَبل اِعتِمادها في أَيّ إجراء قَضائيّ.",
        "لا تُدرِج بَيانات حَسَّاسة لِمُوَكِّليك دون مُوافَقَتِهِم المُسبَقة.",
      ]} />

      <H>سَحب المُوافَقة</H>
      <P>
        مُوافَقَتُك عَلى مُعالَجَة إدخالاتك بِواسِطة الذَّكاء الاصطِناعي دائِمة طالَما
        حِسابك نَشِط. لِسَحب المُوافَقة، يُمكِنك حَذف حِسابك مِن الإعدادات (سَيَحذِف
        الحِساب كامِلاً وَكُلّ البَيانات المُرتَبِطة بِه خِلال 7 أَيّام).
      </P>

      <H>حَدَث لا يَقَع تَحت المُوافَقة</H>
      <P>
        رَفض هٰذِه المُوافَقة لا يَمنَعك مِن استِخدام باقي التَّطبيق: تَصَفُّح
        القَوانين، إدارَة القَضايا وَالمُوَكِّلين، الجَدولة، وَبَوّابَة المُوَكِّلين
        تَعمَل بِشَكل طَبيعي دون أَيّ قُيود.
      </P>

      <H>الإقرار</H>
      <P>
        بِتَأشيرك المُرَبَّع في شاشة المُوافَقة، فأنتَ تُقِرّ بأنّك قَرَأتَ هٰذِه
        السِّياسة، فَهِمتَها، وَوافَقتَ صَراحَةً على مُعالَجَة إدخالاتك بِواسِطة نَماذج
        الذَّكاء الاصطِناعي. الوَقت وَعُنوان IP المُجَزَّأ يُحفَظان كَدَليل قانونيّ.
      </P>
    </div>
  );
}
